import {
  Component,
  DestroyRef,
  effect,
  inject,
  input,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { EMPTY, switchMap, tap } from 'rxjs';
import { WizardSection } from '@app/models/project-setup.models';
import { PersonRoleStore } from '@app/stores/person-role/person-role.store';
import { WizardStore } from '@app/stores/project-setup/wizard.store';
import { CardComponent } from '@app/ui-components/card/card.component';
import { ValidatedDropdownComponent } from '@app/ui-components/validated-dropdown/validated-dropdown.component';
import { TranslatePipe } from '@ngx-translate/core';
import {
  applySectionENestedControlsEnabledState,
  isSectionESpecialPurposeYes,
  SectionEFormGroup,
} from '../+helpers/section-e-form.helper';
import { LegalPersonValidatedFormComponent } from '../../legal-person-validated-form/legal-person-validated-form.component';
import { PersonContactCardComponent } from '../../person-contact-card/person-contact-card.component';

const SECTION_E_PERSON_ROLE_CODE = 'KulonlegesCelGazdEgysegKapcs';

@Component({
  selector: 'app-section-e',
  imports: [
    ReactiveFormsModule,
    ValidatedDropdownComponent,
    LegalPersonValidatedFormComponent,
    PersonContactCardComponent,
    CardComponent,
    TranslatePipe,
  ],
  templateUrl: './section-e.component.html',
  styleUrl: './section-e.component.scss',
})
export class SectionEComponent implements OnInit {
  wizardStore = inject(WizardStore);
  personRoleStore = inject(PersonRoleStore);
  projectId = input.required<string>();
  stepDisabled = input<boolean>(false);
  protected formGroup = signal<FormGroup<SectionEFormGroup> | null>(null);
  protected translationKey = 'projectWizard.sectionEForm.';
  showFormContent = signal<boolean>(false);

  constructor(private destroyRef: DestroyRef) {
    effect(() => {
      this.stepDisabled();
      const form = this.formGroup();
      if (!form) return;
      this.applySectionEConditionalControls(form);
    });
  }

  ngOnInit(): void {
    this.wizardStore
      .getSection(this.projectId(), WizardSection.E)
      .pipe(
        switchMap((data) => {
          const form = data as FormGroup<SectionEFormGroup> | null | undefined;
          if (!form?.controls) {
            return EMPTY;
          }
          this.formGroup.set(form);
          this.syncSpecialPurposeUi(form);
          this.setDefaultContactPersonConnectionTypeCodeId(form);
          this.applySectionEConditionalControls(form);
          return form.controls.specialPurpose.valueChanges.pipe(
            tap(() => {
              this.syncSpecialPurposeUi(form);
              this.setDefaultContactPersonConnectionTypeCodeId(form);
              this.applySectionEConditionalControls(form);
            }),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private syncSpecialPurposeUi(form: FormGroup<SectionEFormGroup>): void {
    this.showFormContent.set(
      isSectionESpecialPurposeYes(form.controls.specialPurpose.value),
    );
  }

  private applySectionEConditionalControls(
    form: FormGroup<SectionEFormGroup>,
  ): void {
    const stepDisabled = this.stepDisabled();
    if (stepDisabled) {
      form.disable({ emitEvent: false });
      return;
    }

    form.enable({ emitEvent: false });
    applySectionENestedControlsEnabledState(
      form,
      isSectionESpecialPurposeYes(form.controls.specialPurpose.value),
    );
  }

  private setDefaultContactPersonConnectionTypeCodeId(
    form: FormGroup<SectionEFormGroup>,
  ): void {
    if (!isSectionESpecialPurposeYes(form.controls.specialPurpose.value)) {
      return;
    }
    const ctrl = form.controls.contactPersonConnectionTypeCodeId;
    if (ctrl.value) return;
    const entity = this.personRoleStore
      .personRoleEntities()
      .find((e) => e.code === SECTION_E_PERSON_ROLE_CODE);
    if (entity) ctrl.setValue(entity.id, { emitEvent: false });
  }
}
